import { DiceKeepGameEvent, DiceRollGameEvent, GameState } from '../../../types/game';
import { computeRollState } from './turnCycleHandlers';
import { allKept } from '../../../domain/dice';
import { makeEmptyDiceMask } from '../../../domain/gameRules';
import { computeStatusAfterKeep } from '../../../domain/turnCycleStateMachine';

interface HandleDiceRollEventParams {
  state: GameState;
  event: DiceRollGameEvent;
  isVerboseLoggingEnabled?: () => boolean;
  logger?: { warn?: (tag: string, payload?: unknown) => void };
  perf?: { inc?: (label: string) => void };
}

export function handleDiceRollEvent({
  state,
  event,
  isVerboseLoggingEnabled,
  logger,
  perf,
}: HandleDiceRollEventParams): GameState {
  const clearedNotices: import('../../../types/game').GameNotice[] = [];
  if (state.mode === 'REAL') {
    return { ...state, events: [...(state.events || []), event], notices: clearedNotices };
  }

  let tc = state.turnCycle;
  const payload = event.payload;
  if (!tc) {
    const firstActive = payload.playerId;
    tc = {
      activePlayerId: firstActive,
      rollCount: 0,
      dice: [],
      kept: makeEmptyDiceMask(),
      capturedMask: makeEmptyDiceMask(),
      status: 'IDLE',
      diceOrigins: [],
    };
    const playerIdx = state.players.findIndex((p) => p.id === firstActive);
    state = {
      ...state,
      turnCycle: tc,
      lobby: state.lobby ? { ...state.lobby, phase: 'IN_GAME' } : state.lobby,
      currentPlayerIndex: playerIdx >= 0 ? playerIdx : state.currentPlayerIndex,
    };
  }
  const ensuredTurnCycle = tc as NonNullable<typeof tc>;
  const { playerId, rollIndex, dice, keptMask, isHotDiceReset } = payload;
  if (ensuredTurnCycle.activePlayerId !== playerId) {
    perf?.inc?.('guard.roll.wrongPlayer');
    return { ...state, events: [...(state.events || []), event], notices: clearedNotices };
  }

  let turnCycleForRoll = ensuredTurnCycle;
  const rollMismatch = rollIndex !== ensuredTurnCycle.rollCount;
  if (rollMismatch) {
    const rehydrateMismatch = (ensuredTurnCycle.dice?.length ?? 0) === 0 && rollIndex < ensuredTurnCycle.rollCount;
    if (rehydrateMismatch) {
      // Snapshot may have rollCount but no dice; allow replay from earlier rollIndex.
      perf?.inc?.('guard.roll.rehydrate');
      turnCycleForRoll = { ...ensuredTurnCycle, rollCount: rollIndex };
    } else {
      const tolerantHotDice = isHotDiceReset && Math.abs(rollIndex - ensuredTurnCycle.rollCount) <= 1;
      if (tolerantHotDice) {
        perf?.inc?.('guard.roll.hotDiceDesync');
        turnCycleForRoll = { ...ensuredTurnCycle, rollCount: rollIndex };
        if (isVerboseLoggingEnabled?.()) {
          logger?.warn?.('ROLL_INDEX_DESYNC_HOT_DICE', {
            eventRollIndex: rollIndex,
            localRollCount: ensuredTurnCycle.rollCount,
            eventId: event.id,
          });
        }
      } else {
        perf?.inc?.('guard.roll.outOfOrder');
        return { ...state, notices: clearedNotices };
      }
    }
  }

  const rollResult = computeRollState({
    state: { ...state, turnCycle: turnCycleForRoll },
    dice,
    keptMask,
    rollIndex,
    eventId: event.id,
    isHotDiceReset,
  });

  const status: typeof ensuredTurnCycle.status = rollResult.status;
  return {
    ...state,
    notices: clearedNotices, // wyczyść poprzednie komunikaty przy nowym rzucie
    segments: rollResult.segments,
    segmentsOwnerId: turnCycleForRoll?.activePlayerId || state.segmentsOwnerId,
    turnCycle: {
      ...turnCycleForRoll,
      dice,
      rollCount: rollIndex + 1,
      kept: keptMask,
      capturedMask: rollResult.capturedMask,
      status,
      lastRoll: rollResult.lastRoll,
      diceOrigins: rollResult.diceOrigins,
    },
    events: [...(state.events || []), event],
  };
}

export function handleDiceKeepEvent(state: GameState, event: DiceKeepGameEvent): GameState {
  if (state.mode === 'REAL' || !state.turnCycle) {
    return { ...state, events: [...(state.events || []), event] };
  }
  const { playerId, rollIndex, keptMask } = event.payload;
  if (state.turnCycle.activePlayerId !== playerId) {
    return { ...state, events: [...(state.events || []), event] };
  }
  const expectedRollIndex = typeof state.turnCycle.lastRoll?.rollIndex === 'number'
    ? state.turnCycle.lastRoll.rollIndex
    : (state.turnCycle.rollCount > 0 ? state.turnCycle.rollCount - 1 : -1);
  if (!Number.isInteger(rollIndex) || rollIndex !== expectedRollIndex) {
    return { ...state, events: [...(state.events || []), event] };
  }
  const validMask = Array.isArray(keptMask)
    && keptMask.length === makeEmptyDiceMask().length
    && keptMask.every((flag) => typeof flag === 'boolean');
  if (!validMask) {
    return { ...state, events: [...(state.events || []), event] };
  }
  const capturedMask = state.turnCycle.capturedMask || makeEmptyDiceMask();
  const normalizedKeptMask = keptMask.map((flag, index) => flag || capturedMask[index]);
  const status = computeStatusAfterKeep(state.turnCycle.status, allKept(normalizedKeptMask));
  return {
    ...state,
    turnCycle: {
      ...state.turnCycle,
      kept: normalizedKeptMask,
      status,
    },
    events: [...(state.events || []), event],
  };
}
